import { darken, decomposeColor, getContrastRatio, lighten } from '@mui/material/styles';
import { BRAND_COLOR_ROLES, BRAND_VARIABLE_KEYS, DEFAULT_BRAND_COLORS, brandVariableName } from './brandTokens';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const SURFACE = '#FFFFFF';
const LIGHT_INK = '#FFFFFF';
const DARK_INK = '#1B1A18';
const TEXT_CONTRAST = 4.5;

export const isValidBrandColor = (value) => typeof value === 'string' && HEX_COLOR.test(value.trim());

export const normalizeBrandColor = (value) => (isValidBrandColor(value) ? value.trim().toUpperCase() : null);

/** Keeps only valid role colours; returns null when nothing is customised. */
export const normalizeBrandColors = (colors) => {
  if (!colors) return null;
  const normalized = BRAND_COLOR_ROLES.reduce((acc, role) => {
    const value = normalizeBrandColor(colors[role] ?? colors[role.charAt(0).toUpperCase() + role.slice(1)]);
    return value ? { ...acc, [role]: value } : acc;
  }, {});
  return Object.keys(normalized).length ? normalized : null;
};

const toHex = (color) => {
  const { values } = decomposeColor(color);
  return `#${values.slice(0, 3).map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
};

const toChannels = (color) => decomposeColor(color).values.slice(0, 3).map(Math.round).join(' ');

/** The candidate ink (light or dark) that reads best on the given background. */
const pickOnColor = (background, lightInk, darkInk) =>
  getContrastRatio(background, lightInk) >= getContrastRatio(background, darkInk) ? lightInk : darkInk;

/**
 * Shifts `color` towards white or black (whichever increases contrast) until it
 * reaches `minContrast` against `against`, or gives up after a full shift.
 */
const ensureContrast = (color, against, minContrast) => {
  if (getContrastRatio(color, against) >= minContrast) return color;
  const shift = getContrastRatio(against, '#FFFFFF') > getContrastRatio(against, '#000000') ? lighten : darken;
  for (let step = 0.1; step <= 1; step += 0.1) {
    const candidate = toHex(shift(color, step));
    if (getContrastRatio(candidate, against) >= minContrast) return candidate;
  }
  return toHex(shift(color, 1));
};

/**
 * Derives the full runtime palette from the four (optional) brand roles.
 * Invalid or missing roles fall back to the Salovare defaults.
 */
export const buildBrandPalette = (colors) => {
  const custom = normalizeBrandColors(colors) || {};
  const primary = custom.primary || DEFAULT_BRAND_COLORS.primary;
  const accent = custom.accent || DEFAULT_BRAND_COLORS.accent;
  const text = custom.text || DEFAULT_BRAND_COLORS.text;
  const background = custom.background || DEFAULT_BRAND_COLORS.background;

  const lightInk = getContrastRatio(background, '#FFFFFF') < 1.2 ? background : LIGHT_INK;
  const darkInk = getContrastRatio(text, '#FFFFFF') >= TEXT_CONTRAST ? text : DARK_INK;

  return {
    primary,
    primaryDark: toHex(darken(primary, 0.22)),
    primaryLight: toHex(lighten(primary, 0.18)),
    primaryText: ensureContrast(primary, background, TEXT_CONTRAST),
    onPrimary: pickOnColor(primary, lightInk, darkInk),
    accent,
    accentDark: toHex(darken(accent, 0.15)),
    accentLight: ensureContrast(accent, primary, TEXT_CONTRAST),
    accentText: ensureContrast(accent, background, TEXT_CONTRAST),
    onAccent: pickOnColor(accent, lightInk, darkInk),
    text: ensureContrast(text, background, TEXT_CONTRAST),
    background,
    surface: SURFACE,
  };
};

/** CSS custom properties (colour + `-rgb` channels) for a derived palette. */
export const buildBrandCssVariables = (palette) =>
  BRAND_VARIABLE_KEYS.reduce((vars, key) => {
    const name = brandVariableName(key);
    return { ...vars, [name]: palette[key], [`${name}-rgb`]: toChannels(palette[key]) };
  }, {});
